import { FormikValues } from 'formik'
import { FC, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import * as yup from 'yup'
import { SimpleFormModal } from '~/components/SimpleFormModal/SimpleFormModal'
import { createAircraft, updateAircraft } from '~/modules/aircraft/data/aircraftApi'
import { IAircraft, ICompanyAsset, initialCompanyAsset } from '~/utils/types'
import {
  assetToCompanyAsset,
  findOrCreateCustomAssetManufacturer,
  findOrCreateCustomAssetModel,
  joinCompanyAssetTranslations,
} from '../functions'
import { CompanyAssetForm } from './CompanyAssetForm'

interface CustomAssetModalProps {
  values?: IAircraft // Asset translation from aircraft to company
  initialValuesAsCustom?: Partial<ICompanyAsset>
  creating: boolean
  open: boolean
  onClose: () => void
  onMutate?: (newItem: IAircraft) => void
}

export const companyAssetValidationSchema = yup.object({
  department_area: yup.string().required('Department/Area is required.').min(3),
  company_plant: yup.string().required('Company Plant is required.').min(3),
  address_line: yup.string().required('Address Line is required.').min(3),
  zip: yup.string(),
  company_name: yup
    .mixed()
    .required('Company Name is required.')
    .test(
      'company-name-filled',
      'Company Name is required.',
      (value) =>
        (typeof value === 'string' && value.trim().length > 0) ||
        (typeof value === 'object' &&
          value !== null &&
          'name' in value &&
          typeof value.name === 'string' &&
          value.name.trim().length > 0),
    ),
})

export const CompanyAssetModal: FC<CustomAssetModalProps> = ({
  values,
  initialValuesAsCustom,
  open,
  onClose,
  creating = true,
  onMutate,
}) => {
  const { t } = useTranslation()
  const initialValues = useMemo((): ICompanyAsset => {
    return {
      ...(values === undefined ? initialCompanyAsset() : assetToCompanyAsset(values)),
      ...initialValuesAsCustom,
    }
  }, [initialValuesAsCustom, values])

  const onSubmit = useCallback(
    async (rawValues: FormikValues) => {
      const formikValues = {
        ...rawValues,
        company_name: rawValues.company_name?.name ?? rawValues.company_name?.toString(),
      } as ICompanyAsset
      const initialTranslated = joinCompanyAssetTranslations(initialValues)
      const translatedValues = joinCompanyAssetTranslations(formikValues)

      if (creating) {
        const manufacturerId = await findOrCreateCustomAssetManufacturer(
          translatedValues.manufacturerName,
        )
        if (manufacturerId === null) return

        const modelId = await findOrCreateCustomAssetModel(
          translatedValues.assetModelName,
          manufacturerId,
        )
        if (modelId === null) return

        const createdAsset = await createAircraft({
          aircraft_model: modelId,
          manufacturerId: manufacturerId,
          serial_number: translatedValues.serial_number,
        } as unknown as IAircraft)

        if (createdAsset) {
          onMutate?.(createdAsset)
          toast.success('SMS & Quality asset successfully created.')

          onClose()
        }

        return
      }

      const manufacturerId = await findOrCreateCustomAssetManufacturer(
        initialTranslated.manufacturerName,
      )

      if (manufacturerId === null) return

      const modelUpdated = initialTranslated.assetModelName !== translatedValues.assetModelName
      const manufacturerUpdated =
        initialTranslated.manufacturerName !== translatedValues.manufacturerName

      let newManufacturerId = null

      if (manufacturerUpdated) {
        newManufacturerId = await findOrCreateCustomAssetManufacturer(
          translatedValues.manufacturerName,
        )
      }

      let newModelId = null

      if (modelUpdated || newManufacturerId) {
        newModelId = await findOrCreateCustomAssetModel(
          translatedValues.assetModelName,
          newManufacturerId ?? manufacturerId,
        )
      }

      const snUpdated = initialTranslated.serial_number !== translatedValues.serial_number

      if ((snUpdated || newModelId) && values?._id) {
        await updateAircraft({
          aircraft_id: values._id,
          aircraft: {
            serial_number: translatedValues.serial_number,
            audits: [],
            aircraft_model: newModelId ?? undefined,
          } as unknown as IAircraft,
        })
      }

      onMutate?.(values as IAircraft)
      toast.success('SMS & Quality asset successfully updated.')
      onClose()
    },
    [creating, initialValues, onClose, onMutate, values],
  )

  if (!open) return null

  return (
    <SimpleFormModal
      form={{
        initialValues: initialValues,
        onSubmit,
        validationSchema: companyAssetValidationSchema,
      }}
      open={open}
      onClose={onClose}
      title={`${creating ? t('Creating') : t('Editing')} ${t('SMS & Quality Asset')}`}
    >
      <CompanyAssetForm />
    </SimpleFormModal>
  )
}
